import React, { useCallback, useEffect, useState } from 'react';
import { Loading, Title, useNotify, useTranslate } from 'react-admin';
import { Link } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import TuneIcon from '@mui/icons-material/Tune';

import {
    deleteFocusItem, fetchFocusBoard, postFocusCompute, postFocusItem, putFocusCard, putFocusItem, reorderFocusItems,
} from '../../services/api';
import Pill from '../reviews/Pill';
import { CardDialog, ItemDialog } from './FocusDialogs';
import { AlertsBanner, BoardFilters, ClosedItems, TeamSummary, TrendChart, matchesSearch, sortCards } from './FocusExtras';

export const LOAD_COLORS = { low: 'info', normal: 'success', high: 'warning', overloaded: 'error' };
export const PRIORITY_COLORS = { high: 'error', medium: 'warning', low: 'default' };

// Under 70 % the week is not filled, over 110 % it overflows.
export const loadColor = (pct, upper = 110) => {
    if (pct === null || pct === undefined) return 'default';
    if (pct < 70) return 'warning';
    if (pct > upper) return 'error';
    return 'success';
};

// Share of the due days that have at least one line. Under 80 % the timesheet is behind.
export const completionColor = (pct) => {
    if (pct === null || pct === undefined) return 'default';
    if (pct >= 90) return 'success';
    if (pct >= 80) return 'warning';
    return 'error';
};

export const qualityColor = (score) => {
    if (score === null || score === undefined) return 'default';
    if (score >= 85) return 'success';
    if (score >= 65) return 'warning';
    return 'error';
};

export const days = (value) => (value === null || value === undefined ? '-' : `${Number(value).toLocaleString()} j`);

export const initials = (user) => (user.fullname || user.trigram || '?').trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();

export const Meter = ({ label, value, hint, upper }) => {
    const theme = useTheme();
    const color = loadColor(value, upper);
    const barColor = color === 'default' ? theme.palette.text.disabled : theme.palette[color].main;
    return (
        <Tooltip title={hint || ''}>
            <Box>
                <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: barColor }}>{value ?? '-'}{value !== null && value !== undefined ? ' %' : ''}</Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={Math.min(value || 0, 150) / 1.5}
                    sx={{ height: 6, borderRadius: 3, backgroundColor: alpha(barColor, 0.15), '& .MuiLinearProgress-bar': { backgroundColor: barColor } }}
                />
            </Box>
        </Tooltip>
    );
};

export const ItemLine = ({ item, canEdit, first, last, onEdit, onDelete, onMove, detailed }) => {
    const translate = useTranslate();
    const barColor = PRIORITY_COLORS[item.priority] === 'default' ? 'action.disabled' : `${PRIORITY_COLORS[item.priority]}.main`;
    return (
        <Box sx={{ display: 'flex', gap: 1, padding: '6px 0', borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } }}>
            <Box sx={{ width: 6, alignSelf: 'stretch', borderRadius: 3, flexShrink: 0, bgcolor: barColor }} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box display="flex" alignItems="flex-start" gap={1}>
                    <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 600, textDecoration: item.status === 'done' ? 'line-through' : 'none' }}>{item.title}</Typography>
                    {item.effort_week_days ? <Pill label={`${Number(item.effort_week_days).toLocaleString()} ${translate('d/wk')}`} color="primary" variant="outlined" /> : null}
                </Box>
                {detailed && (
                    <>
                        {item.description && <Typography variant="caption" color="text.secondary" component="p" sx={{ whiteSpace: 'pre-line' }}>{item.description}</Typography>}
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                            <Typography variant="caption" color="text.secondary">
                                {translate('Total')} {days(item.effort_total_days)}
                                {item.spent_days !== null && item.spent_days !== undefined ? ` · ${translate('spent')} ${days(item.spent_days)}` : ''}
                                {item.start_date ? ` · ${item.start_date}${item.end_date ? ` → ${item.end_date}` : ''}` : ''}
                                {item.status !== 'active' ? ` · ${translate(`focus_status.${item.status}`, { _: item.status })}` : ''}
                                {item.project_names?.length ? ` · ${item.project_names.join(', ')}` : ''}
                            </Typography>
                            {canEdit && (
                                <Box display="flex" sx={{ flexShrink: 0 }}>
                                    <IconButton size="small" disabled={first} onClick={() => onMove(-1)}><ArrowUpwardIcon fontSize="inherit" /></IconButton>
                                    <IconButton size="small" disabled={last} onClick={() => onMove(1)}><ArrowDownwardIcon fontSize="inherit" /></IconButton>
                                    <IconButton size="small" onClick={onEdit}><EditOutlinedIcon fontSize="inherit" /></IconButton>
                                    <IconButton size="small" onClick={onDelete}><DeleteOutlineIcon fontSize="inherit" /></IconButton>
                                </Box>
                            )}
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

export const QuickAdd = ({ onAdd }) => {
    const translate = useTranslate();
    const [title, setTitle] = useState('');
    const [week, setWeek] = useState('');

    const submit = (event) => {
        event.preventDefault();
        if (!title.trim()) return;
        onAdd({ title: title.trim(), effort_week_days: week === '' ? null : Number(week) });
        setTitle('');
        setWeek('');
    };

    return (
        <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: 1, alignItems: 'center', marginTop: '8px', padding: '2px 8px', borderRadius: '8px', border: '1px dashed', borderColor: 'divider' }}>
            <AddIcon fontSize="small" color="action" />
            <InputBase sx={{ flexGrow: 1, fontSize: 14 }} placeholder={translate('Add a priority')} value={title} onChange={(event) => setTitle(event.target.value)} />
            <InputBase sx={{ width: 64, fontSize: 14 }} placeholder={translate('d/wk')} type="number" inputProps={{ step: 0.5, min: 0, max: 7 }} value={week} onChange={(event) => setWeek(event.target.value)} />
            <Button type="submit" size="small" variant="contained" disabled={!title.trim()} sx={{ minWidth: 0 }}>
                {translate('Add')}
            </Button>
        </Box>
    );
};

const FocusCardView = ({ card, canEdit, expanded, onToggle, onChange }) => {
    const translate = useTranslate();
    const theme = useTheme();
    const notify = useNotify();
    const [cardDialog, setCardDialog] = useState(false);
    const [itemDialog, setItemDialog] = useState(null);

    const run = async (action) => {
        try {
            await action();
            onChange();
        } catch (error) {
            notify(error.message, { type: 'error' });
        }
    };

    const items = card.items || [];
    const plannedWeek = items.reduce((sum, item) => sum + (item.effort_week_days || 0), 0);
    const move = (index, direction) => {
        const ids = items.map(item => item.id);
        const [moved] = ids.splice(index, 1);
        ids.splice(index + direction, 0, moved);
        run(() => reorderFocusItems(card.user_id, ids));
    };
    const avatarColor = card.user.color || theme.palette.primary.main;
    const quality = card.timesheet_quality;
    const completion = card.timesheet_quality_details?.coverage_pct;

    return (
        <Card variant="outlined" sx={{ borderRadius: '12px', borderTop: '4px solid', borderTopColor: avatarColor, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ paddingBottom: '12px !important' }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{ bgcolor: avatarColor, width: 40, height: 40, fontSize: 16 }}>{initials(card.user)}</Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography component={Link} to={`/focus/${card.user_id}`} sx={{ fontWeight: 600, lineHeight: 1.2, color: 'text.primary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                            {card.user.fullname}
                        </Typography>
                        <Typography variant="body2" color={card.headline ? 'text.primary' : 'text.disabled'} noWrap title={card.headline || ''}>
                            {card.headline || translate('No headline yet')}
                        </Typography>
                    </Box>
                    {canEdit && <IconButton size="small" onClick={() => setCardDialog(true)}><TuneIcon fontSize="small" /></IconButton>}
                    <IconButton size="small" onClick={onToggle}>{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, marginTop: '14px' }}>
                    <Meter label={translate('Measured load')} value={card.load_logged_pct}
                        hint={translate('Hours logged over hours due on the last 4 weeks (leaves and holidays deducted)')} />
                    <Meter label={translate('Planned load')} value={card.load_planned_pct} upper={100}
                        hint={translate('Days per week of the active priorities over the capacity')} />
                </Box>
                <Box display="flex" gap={0.75} flexWrap="wrap" sx={{ marginTop: '10px' }}>
                    <Tooltip title={translate('My perception of the load')}>
                        <span><Pill label={`${translate('Perceived')} : ${card.manager_load ? translate(`focus_load.${card.manager_load}`, { _: card.manager_load }) : '-'}`}
                            color={LOAD_COLORS[card.manager_load] || 'default'} variant="outlined" /></span>
                    </Tooltip>
                    <Tooltip title={translate('Days with at least one line, over the days due (leaves and holidays deducted)')}>
                        <span><Pill label={`${translate('Completion')} ${completion ?? '-'}${completion !== null && completion !== undefined ? ' %' : ''}`}
                            color={completionColor(completion)} variant="outlined" /></span>
                    </Tooltip>
                    <Tooltip title={translate('Timesheet quality: precise, on time, complete, typed, sized, not rounded')}>
                        <span><Pill label={`${translate('Timesheet')} ${quality ?? '-'}${quality !== null && quality !== undefined ? '/100' : ''}`} color={qualityColor(quality)} variant="outlined" /></span>
                    </Tooltip>
                    <Tooltip title={translate('Projects carried (8 h or more) / touched, and client projects')}>
                        <span><Pill label={`${card.projects_carried ?? '-'}/${card.projects_touched ?? '-'} ${translate('projects')} · ${card.client_projects ?? '-'} ${translate('clients')}`} variant="outlined" /></span>
                    </Tooltip>
                </Box>

                <AlertsBanner alerts={card.alerts} />

                <Box sx={{ marginTop: '12px' }}>
                    {items.length === 0 && <Typography variant="body2" color="text.disabled">{translate('No priority yet')}</Typography>}
                    {(expanded ? items : items.slice(0, 3)).map((item, index) => (
                        <ItemLine
                            key={item.id}
                            item={item}
                            canEdit={canEdit}
                            detailed={expanded}
                            first={index === 0}
                            last={index === items.length - 1}
                            onMove={(direction) => move(index, direction)}
                            onEdit={() => setItemDialog(item)}
                            onDelete={() => window.confirm(translate('Delete this priority?')) && run(() => deleteFocusItem(item.id))}
                        />
                    ))}
                    {!expanded && items.length > 3 && (
                        <Button size="small" onClick={onToggle}>+ {items.length - 3} {translate('more')}</Button>
                    )}
                    {items.length > 0 && (
                        <Typography variant="caption" color="text.secondary" component="p" sx={{ marginTop: '4px', textAlign: 'right' }}>
                            {translate('Planned')} : {plannedWeek.toLocaleString()} / {Number(card.capacity_days_week).toLocaleString()} {translate('d/wk')}
                        </Typography>
                    )}
                    {canEdit && <QuickAdd onAdd={(data) => run(() => postFocusItem({ user_id: card.user_id, ...data }))} />}
                </Box>

                <Collapse in={expanded} unmountOnExit>
                    <ClosedItems
                        items={card.closed_items}
                        renderItem={(item) => (
                            <ItemLine key={item.id} item={item} canEdit={canEdit} detailed first last
                                onMove={() => {}} onEdit={() => setItemDialog(item)}
                                onDelete={() => window.confirm(translate('Delete this priority?')) && run(() => deleteFocusItem(item.id))} />
                        )}
                    />
                    <TrendChart userId={card.user_id} />
                    {card.ai_summary && (
                        <Box sx={{ marginTop: '14px', padding: '8px 10px', borderRadius: '8px', backgroundColor: alpha(theme.palette.primary.main, 0.06), display: 'flex', gap: 1 }}>
                            <AutoAwesomeIcon fontSize="small" color="primary" />
                            <Typography variant="body2">{card.ai_summary}</Typography>
                        </Box>
                    )}
                    {card.top_projects?.length > 0 && (
                        <Box sx={{ marginTop: '14px' }}>
                            <Typography variant="subtitle2">{translate('Where the time went (4 weeks)')}</Typography>
                            {card.top_projects.map(project => (
                                <Box key={project.project_id ?? project.name} sx={{ marginTop: '6px' }}>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="caption" noWrap>{project.name}{project.internal ? ` · ${translate('internal')}` : ''}</Typography>
                                        <Typography variant="caption" color="text.secondary">{project.hours} h · {project.pct} %</Typography>
                                    </Box>
                                    <LinearProgress variant="determinate" value={project.pct} sx={{ height: 4, borderRadius: 2 }} color={project.internal ? 'inherit' : 'primary'} />
                                </Box>
                            ))}
                        </Box>
                    )}
                    {card.manager_note && (
                        <Typography variant="body2" sx={{ marginTop: '14px', whiteSpace: 'pre-line', fontStyle: 'italic' }}>{card.manager_note}</Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ marginTop: '12px' }}>
                        {card.hours_logged ?? '-'} h / {card.hours_expected ?? '-'} h {translate('due')}
                        {card.window_start ? ` · ${card.window_start} → ${card.window_end}` : ''}
                        {card.computed_at ? ` · ${translate('computed on')} ${card.computed_at.slice(0, 10)}` : ` · ${translate('not computed yet')}`}
                    </Typography>
                </Collapse>
            </CardContent>

            {canEdit && (
                <>
                    <CardDialog card={card} open={cardDialog} onClose={() => setCardDialog(false)}
                        onSave={(data) => { setCardDialog(false); run(() => putFocusCard(card.user_id, data)); }} />
                    <ItemDialog item={itemDialog} open={Boolean(itemDialog)} suggestions={card.top_projects} onClose={() => setItemDialog(null)}
                        onSave={(data) => { const item = itemDialog; setItemDialog(null); run(() => putFocusItem(item.id, data)); }} />
                </>
            )}
        </Card>
    );
};

const FocusBoard = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [board, setBoard] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [computing, setComputing] = useState(false);
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('name');
    const [withoutPriority, setWithoutPriority] = useState(false);

    const load = useCallback(() => {
        fetchFocusBoard().then(setBoard).catch(error => notify(error.message, { type: 'error' }));
    }, [notify]);

    useEffect(() => {
        load();
    }, [load]);

    if (!board) {
        return <Loading />;
    }

    const visibleCards = sortCards(
        board.results.filter(card => matchesSearch(card, search) && (!withoutPriority || card.items.length === 0)),
        sort,
    );
    const allExpanded = board.results.length > 0 && board.results.every(card => expanded[card.user_id]);
    const toggleAll = () => setExpanded(allExpanded ? {} : Object.fromEntries(board.results.map(card => [card.user_id, true])));
    const recompute = async () => {
        setComputing(true);
        try {
            await postFocusCompute();
            load();
        } catch (error) {
            notify(error.message, { type: 'error' });
        } finally {
            setComputing(false);
        }
    };

    return (
        <Box sx={{ marginTop: '16px' }}>
            <Title title="Focus" />
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ marginBottom: '16px' }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>Focus</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {translate('Who is on what, high level. Loads are measured from the timesheet, not declared.')}
                    </Typography>
                </Box>
                <Box display="flex" gap={1}>
                    <Button onClick={toggleAll} startIcon={allExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
                        {allExpanded ? translate('Collapse all') : translate('Expand all')}
                    </Button>
                    {board.can_edit && (
                        <Button variant="outlined" onClick={recompute} disabled={computing} startIcon={<RefreshIcon />}>
                            {computing ? translate('Computing…') : translate('Recompute')}
                        </Button>
                    )}
                </Box>
            </Box>
            <TeamSummary summary={board.summary} />
            <BoardFilters search={search} onSearch={setSearch} sort={sort} onSort={setSort}
                withoutPriority={withoutPriority} onWithoutPriority={setWithoutPriority} />
            {visibleCards.length === 0 && (
                <Typography color="text.secondary">{translate('Nobody matches these filters.')}</Typography>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 2, alignItems: 'start' }}>
                {visibleCards.map(card => (
                    <FocusCardView
                        key={card.user_id}
                        card={card}
                        canEdit={board.can_edit}
                        expanded={Boolean(expanded[card.user_id])}
                        onToggle={() => setExpanded({ ...expanded, [card.user_id]: !expanded[card.user_id] })}
                        onChange={load}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default FocusBoard;
